/* ========================================================================== *\
___________       __         .__
\_   _____/ _____/  |________|__| ____
 |    __)_ /    \   __\_  __ \  |/  _ \
 |        \   |  \  |  |  | \/  (  <_> )
/_______  /___|  /__|  |__|  |__|\____/
        \/     \/
\* ========================================================================== */

/*
 * ProductAiImage.jsx — สร้าง/แก้ภาพสินค้าด้วย AI   (งานข้อ 4 · Step 5.3)
 *
 * หลักเดียวกับ ProductAiInfo.jsx — ถือ state ของตัวเอง เรียก endpoint ของตัวเอง
 * ไม่แตะฟอร์มสินค้าเดิม (กัน mass assignment ใน product.con.php:377)
 *
 * ออกแบบรอบ 3 (6 ต.ค. 2569 ดึก) — เจ้าของงานส่งภาพเครื่องมือสร้างภาพแบบ "แผงเดียว" มาให้ทำตาม
 *   ซ้าย  = แผงสั่งงานแนวตั้ง: โมเดล → รูปอ้างอิง (n/เพดาน) → คำสั่ง (+ ปุ่มเทมเพลต) → สัดส่วน/ความละเอียด → ปุ่มสร้าง
 *   ขวา   = ผลงาน
 *   ไม่มีคำว่า "โหมด" อีกแล้ว: มีรูปอ้างอิง = แก้จากรูป (IMAGE_EDIT) · ไม่มี = AI วาดใหม่ (IMAGE_GEN)
 *   รูปอ้างอิงผสมได้ทั้งรูปในแกลเลอรีสินค้าและรูปที่อัปโหลด เพดานตามโมเดล (nano-banana-2 14 · GPT 16)
 *   เปิดหน้ามาใส่รูปหลักของสินค้าเป็นรูปอ้างอิงให้แล้ว → งานปกติ = กดเทมเพลต แล้วกดสร้าง
 *
 * ตามมติประชุม 14 ส.ค.: ต้องมีปุ่มแก้ไขและปุ่มลบภาพ
 *   แก้ไข = สร้างรอบใหม่จากภาพเดิมด้วย prompt ใหม่ (PATCH /product_ai_image/<id>)
 *   ลบ    = ซ่อนรายการ ประวัติค่าใช้จ่ายยังอยู่ (DELETE /product_ai_image/<id>)
 *
 * การ poll สถานะ: ทุก 6 วินาที และแนบ _t กันด่าน preventDuplicate ของระบบเดิม
 *
 * แผงสั่งงานจำค่าไว้ต่อผู้ใช้+สินค้า (หน่วยความจำ + sessionStorage อายุ 8 ชม. แบบ ProductAiInfo)
 *   แท็บถูก unmount ตอนสลับแท็บ/กดเปิดแกลเลอรี ค่าที่ใส่ไว้เคยหายหมด · ล้างเมื่อส่งงานสำเร็จ (18 ต.ค. 2569)
 *
 * เส้นทางที่ใช้
 *   GET     /product_ai_image             ค่าตัวเลือก + สิทธิ์ + โควตา + เทมเพลต (presets)
 *   OPTIONS /product_ai_image/by_product  รายการของสินค้านี้ + รูปแกลเลอรี (product.images)
 *   PUT     /product_ai_image             สั่งสร้าง source=refs {refs:[{t:'g',id}|{t:'u',d}]} หรือ none
 *   OPTIONS /product_ai_image/poll        เช็คสถานะ + ดาวน์โหลดเมื่อเสร็จ
 *   OPTIONS /product_ai_image/approve     บันทึกเข้าแกลเลอรีสินค้า
 */

import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

// @mui material components
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import ListItemIcon from '@mui/material/ListItemIcon';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import ButtonBase from '@mui/material/ButtonBase';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDProgress from 'components/MDProgress';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import ImageReview from 'layouts/_entrio/ImageReview';
import TemplateManager from 'layouts/_entrio/TemplateManager';
import MDSnackbar from 'components/MDSnackbar';

import Resizer from 'react-image-file-resizer';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';
import GalleryLink from 'layouts/_entrio/GalleryLink';
import ImagePickerDialog from 'layouts/_entrio/ImagePickerDialog';
import nopic from 'assets/images/nopic.jpg';
import {
  ReviewThumb,
  CardMenu,
  ActionRow,
  DownloadButton,
  SelectBar,
  iconBtnSx,
} from 'layouts/_entrio/ResultCardParts';
import useBulkDownload from 'layouts/_entrio/useBulkDownload';
import promptLength from 'layouts/_entrio/promptLength';

/* เครดิตต่อภาพตามความละเอียดที่เลือก (migration 0024 · backend ส่ง credits = {1K,2K,4K})
   ยังไม่รัน 0024 → ไม่มี credits ใช้ credits_per_image แทน (เจ้าของงานทัก 6 ต.ค. 2569 ว่าเปลี่ยน 4K แล้วตัวเลขไม่ขยับ) */
const creditOf = (p, res) => {
  if (!p) return 0;
  const map = p.credits || {};
  return Number(map[res] != null ? map[res] : p.credits_per_image) || 0;
};

const statusList = {
  PENDING: { name: 'กำลังสร้าง', color: 'warning' },
  RUNNING: { name: 'กำลังสร้าง', color: 'warning' },
  SUCCESS: { name: 'สำเร็จ', color: 'success' },
  FAILED: { name: 'ไม่สำเร็จ', color: 'error' },
  CANCELLED: { name: 'ยกเลิก', color: 'secondary' },
};

/* หมวดเทมเพลตที่เกี่ยวกับภาพสินค้าขึ้นก่อน · หมวดอื่นรวมเป็น "โปสเตอร์ / แบรนด์" */
const TEMPLATE_CAT_ORDER = ['ของร้าน', 'พื้นหลัง', 'บรรยากาศ', 'มุมกล้อง', 'โปรโมต']; // ของร้าน = ร้านเพิ่มเอง (ai_template · 20 ต.ค. 2569)
const CAT_COLOR = {
  พื้นหลัง: '#5B8DEF',
  บรรยากาศ: '#4CAF93',
  มุมกล้อง: '#F2A541',
  โปรโมต: '#E85D75',
  เทศกาล: '#9C6ADE',
  สีแบรนด์: '#E22320',
};
const RATIO_LABELS = {
  '1:1': 'จัตุรัส',
  '4:5': 'โพสต์แนวตั้ง',
  '3:4': 'แนวตั้ง',
  '9:16': 'สตอรี่',
  '16:9': 'แนวนอน',
  '4:3': 'แนวนอน',
  '3:2': 'แนวนอน',
  '2:3': 'แนวตั้ง',
  '5:4': 'เกือบจัตุรัส',
  '21:9': 'แบนเนอร์',
};
const QUICK_COUNT = 4; // ชิปเทมเพลตลัดใต้ช่องคำสั่ง
const MAX_FETCH_TRIES = 50; // poll งานสำเร็จที่ยังไม่มีไฟล์ได้กี่รอบ (~5 นาที)

/* รหัส error จาก backend → ข้อความไทย (16 ต.ค. 2569 — เดิมหลายรหัสขึ้นแค่ "บันทึกไม่สำเร็จ" ผู้ใช้ไม่รู้ต้องทำอะไร) */
const AI_ERROR_TEXT = {
  not_owner: 'ภาพนี้เป็นของคนอื่น — จัดการได้เฉพาะเจ้าของภาพหรือผู้ดูแลระบบ AI',
  ai_quota_not_set:
    'AI ปิดทั้งร้านอยู่ (โควตาเป็น 0) — ผู้ดูแลระบบตั้งโควตาได้ที่เมนู AI · ค่าใช้จ่ายและโควตา',
  ai_quota_exceeded: 'เกินโควตาประจำเดือนแล้ว',
  ai_user_quota_exceeded: 'เครดิต AI เดือนนี้ของคุณครบตามที่ผู้ดูแลตั้งไว้แล้ว — ติดต่อผู้ดูแลระบบ',
  ai_kill_switch_on:
    'AI ปิดทั้งร้านอยู่ (หยุดฉุกเฉิน) — เปิดกลับได้ที่เมนู AI · ค่าใช้จ่ายและโควตา',
  ai_upload_failed: 'ส่งรูปอ้างอิงให้ AI ไม่สำเร็จ ลองใหม่อีกครั้ง',
  ai_source_file_too_large: 'รูปอ้างอิงไฟล์ใหญ่เกินไป ใช้รูปที่เล็กลง',
  ai_create_failed: 'ผู้ให้บริการ AI ไม่รับงาน ลองใหม่อีกครั้ง',
  ai_api_key_missing: 'ร้านยังไม่ได้เชื่อมต่อผู้ให้บริการ AI — แจ้งไอที',
  permission_denied: 'บัญชีนี้ไม่มีสิทธิ์ใช้งานส่วนนี้',
  ai_prompt_too_long: 'คำสั่งเกิน 2,000 ตัวอักษร ลดข้อความแล้วลองอีกครั้ง',
  ai_prompt_required: 'พิมพ์คำสั่งก่อน',
  ai_image_not_found: 'ไม่พบรูปในแกลเลอรีที่เลือก โหลดหน้าใหม่แล้วลองอีกครั้ง',
  ai_source_gen_not_ready: 'ภาพที่จะแก้ยังไม่พร้อม รอสักครู่แล้วลองใหม่',
  ai_result_not_ready: 'ภาพยังไม่พร้อม รอให้ภาพขึ้นก่อนแล้วลองอีกครั้ง',
  ai_copy_failed: 'คัดลอกภาพเข้าแกลเลอรีไม่สำเร็จ ลองอีกครั้ง',
  ai_provider_not_found: 'โมเดลที่เลือกถูกปิดใช้งานแล้ว เลือกโมเดลใหม่',
  ai_source_data_invalid: 'รูปอ้างอิงที่อัปโหลดใช้ไม่ได้ เอาออกแล้วใส่ใหม่',
  ai_source_file_not_found: 'ไม่พบไฟล์รูปอ้างอิง โหลดหน้าใหม่แล้วลองอีกครั้ง',
  ai_generation_not_found: 'ไม่พบงานนี้แล้ว โหลดหน้าใหม่',
};

/* error_msg ของงานที่ล้มเป็นข้อความอิสระ (failMsg ของผู้ให้บริการ · คำตอบตอนส่งงาน · timeout ของ worker) ไม่ใช่รหัส
   จับคำที่พบบ่อยเป็นไทย ที่เหลือขึ้นข้อความกลาง · ข้อความเดิมอยู่ใน title (18 ต.ค. 2569) */
const failText = (msg) => {
  const m = String(msg || '').trim();
  if (AI_ERROR_TEXT[m]) return AI_ERROR_TEXT[m];
  if (/timeout|timed out/i.test(m)) return 'AI ใช้เวลานานเกินไป';
  if (/credit|insufficient|balance/i.test(m)) return 'เครดิต AI ของร้านไม่พอ แจ้งผู้ดูแลระบบ';
  if (/sensitive|flagged|policy|safety|violat|nsfw|moderat/i.test(m)) {
    return 'AI ไม่รับคำสั่งหรือรูปนี้ ลองปรับคำสั่ง';
  }
  return 'สร้างภาพไม่สำเร็จ';
};

/* แผงสั่งงานจำค่าไว้ — หน่วยความจำเก็บครบ (สลับแท็บ) · sessionStorage เก็บรูปอัปโหลดรวมไม่เกิน ~3 ล้านตัวอักษร
   กันพื้นที่เต็ม (รีโหลดหน้า รูปอัปโหลดที่เกินจะหาย รูปจากแกลเลอรีเก็บแค่ id) */
const panelMemory = new Map();
const PANEL_TTL = 8 * 60 * 60 * 1000;
const UPLOAD_STORE_LIMIT = 2000000; // ต่ำกว่าโควตา sessionStorage ของ Chrome (~2.6M ตัวอักษร) ไม่งั้นเขียนไม่ผ่านแล้วทิ้งรูปทั้งชุด

const readPanel = (key) => {
  let saved = panelMemory.get(key);
  if (!saved) {
    try {
      saved = JSON.parse(sessionStorage.getItem(key));
    } catch (_) {
      saved = null;
    }
  }
  const d = saved && saved.data;
  if (!d || !(Date.now() - saved.at < PANEL_TTL) || typeof d.prompt !== 'string') return null;
  const str = (v, def) => (typeof v === 'string' && v ? v : def);
  return {
    prompt: d.prompt,
    template: str(d.template, ''),
    ratio: str(d.ratio, '1:1'),
    resolution: str(d.resolution, '1K'),
    provider: str(d.provider, 'auto'),
    refs: (Array.isArray(d.refs) ? d.refs : []).filter(
      (r) =>
        r &&
        typeof r.src === 'string' &&
        (((r.t === 'g' || r.t === 'a') && r.id != null) ||
          (r.t === 'u' && r.src.startsWith('data:image/')))
    ),
  };
};

const writePanel = (key, entry) => {
  let budget = UPLOAD_STORE_LIMIT;
  const refs = entry.data.refs.filter((r) => {
    if (r.t !== 'u') return true;
    budget -= r.src.length;
    return budget >= 0;
  });
  const put = (list) =>
    sessionStorage.setItem(
      key,
      JSON.stringify({ at: entry.at, data: { ...entry.data, refs: list } })
    );
  try {
    put(refs);
  } catch (_) {
    try {
      put(refs.filter((r) => r.t === 'g'));
    } catch (e) {
      /* พื้นที่เต็ม — ยังอยู่ในหน่วยความจำจนกว่าจะรีโหลดหน้า */
    }
  }
};

const clearPanel = (key) => {
  panelMemory.delete(key);
  try {
    sessionStorage.removeItem(key);
  } catch (_) {
    /* ไม่มีอะไรต้องล้าง */
  }
};

/* ปุ่มที่ทำจาก ButtonBase ไม่มีกรอบโฟกัสของตัวเอง — ใส่ให้เห็นตอนใช้คีย์บอร์ด */
const focusSx = { '&.Mui-focusVisible': { outline: '2px solid #1A73E8', outlineOffset: 2 } };
const onImgError = (e) => {
  e.currentTarget.onerror = null;
  e.currentTarget.src = nopic;
};

/* ป้ายหัวข้อเล็กในแผง (แบบภาพตัวอย่างที่เจ้าของงานส่งมา) */
function Label({ children, right }) {
  return (
    <MDBox display='flex' justifyContent='space-between' alignItems='center' mb={1}>
      <MDTypography variant='caption' fontWeight='bold' color='text'>
        {children}
      </MDTypography>
      {right ? (
        <MDTypography variant='caption' color='text' sx={{ opacity: 0.7 }}>
          {right}
        </MDTypography>
      ) : null}
    </MDBox>
  );
}
Label.defaultProps = { right: '' };
Label.propTypes = {
  children: PropTypes.node.isRequired,
  right: PropTypes.string,
};

let refSeq = 0;
const withKey = (r) => {
  refSeq += 1;
  return { ...r, key: `r${refSeq}` };
};

function ProductAiImage({ onOpenGallery }) {
  const mod = 'product_ai_image';
  const app = useSelector((state) => state[APP_STATE]);
  const { t } = useTranslation();
  const { id } = useParams();

  const [prompt, setPrompt] = useState('');
  const [template, setTemplate] = useState(''); // code ของเทมเพลตที่ใช้ล่าสุด
  const [refs, setRefs] = useState([]); // รูปอ้างอิง [{key, t:'g'|'u', id?, src}] — รูปแรก = รูปหลัก
  const [resolution, setResolution] = useState('1K');
  const [ratio, setRatio] = useState('1:1');
  const [provider, setProvider] = useState('auto'); // auto = โมเดลตั้งต้นของชนิดงาน (backend เลือกให้)
  const [images, setImages] = useState([]); // รูปในแกลเลอรีสินค้า (by_product.product.images)
  const fileRef = useRef(null);
  const promptRef = useRef(null);
  const refsInit = useRef(false);
  const restored = useRef(false); // เปิดแท็บมาแล้วได้ค่าแผงเดิมคืน → ไม่ใส่รูปหลักทับ
  const stateKey = `entrio:ai-image:v1:${(app.user && app.user.id) || 0}:${id}`;
  const [readyKey, setReadyKey] = useState(''); // คืนค่าแผงของสินค้านี้แล้ว ถึงเริ่มจำค่าใหม่
  const saveTimer = useRef(null);
  const notifyTimer = useRef(null);

  const [addAnchor, setAddAnchor] = useState(null); // เมนู "+ เพิ่ม"
  const [galleryOpen, setGalleryOpen] = useState(false); // dialog เลือกรูปจากแกลเลอรี (ImagePickerDialog ตัวเดียวกับหน้าจัดการภาพ)
  const [tplOpen, setTplOpen] = useState(false); // dialog เทมเพลต
  const [tplCat, setTplCat] = useState('');

  const [meta, setMeta] = useState({
    providers: [],
    resolutions: [],
    ratios: [],
    presets: [],
    can_generate: 'N',
    usage: null,
  });
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0); // ทั้งหมดที่มี · แสดงทีละ 50 กด "แสดงเพิ่ม" (ตรวจการใช้งาน 1.8 · 1 พ.ย. 2569)
  const limitRef = useRef(50);
  /* ผลงานเห็นเฉพาะของตัวเอง · ผู้ดูแล AI (ai_setting) เปิด "ดูของทุกคน" ได้ (27 ต.ค. 2569 · เหมือนหน้ากราฟิกแคมเปญ) */
  const [canViewAll, setCanViewAll] = useState(false);
  const [scopeAll, setScopeAll] = useState(false);
  const scopeRef = useRef(false); // poll เรียก loadList จาก closure เก่า — อ่านค่าล่าสุดจาก ref
  const [reviewId, setReviewId] = useState(null);
  const [productSku, setProductSku] = useState('');
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState([]);
  const approvingIds = useRef(new Set());
  const generating = useRef(false); // กันกดสร้างซ้ำระหว่างรอคำตอบ (เสียเงินซ้ำ) — state อัปเดตไม่ทันดับเบิลคลิก
  const fetchTries = useRef({}); // id งาน → จำนวนรอบที่ poll หาไฟล์แล้ว
  const [lostIds, setLostIds] = useState([]); // งานสำเร็จที่ poll ครบรอบแล้วยังไม่ได้ไฟล์
  const [galleryMessage, setGalleryMessage] = useState('');

  const [editRow, setEditRow] = useState(null);
  const [editPrompt, setEditPrompt] = useState('');
  const [editProvider, setEditProvider] = useState('auto');
  const [editRatio, setEditRatio] = useState('1:1');
  const [editResolution, setEditResolution] = useState('1K');
  const [delRow, setDelRow] = useState(null);

  /* กัน setState หลัง unmount — ตั้ง true ตอน mount ด้วย (Fast Refresh รัน cleanup แล้วค้าง false · เจอ 5 ต.ค.) */
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      clearTimeout(notifyTimer.current);
    };
  }, []);

  /* error/เตือนค้าง 8 วิ ให้อ่านทัน · สำเร็จ/แจ้งทั่วไป 3 วิ — ล้างตัวจับเวลาเก่า ไม่ให้ปิดข้อความใหม่ก่อนเวลา (18 ต.ค. 2569) */
  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }} // มุมล่างขวาบังปุ่มปิดหน้าต่างตรวจภาพ
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(null)}
      />
    );
    clearTimeout(notifyTimer.current);
    notifyTimer.current = setTimeout(
      function () {
        if (alive.current) setNotify(null);
      },
      color === 'error' ? 8000 : 3000
    );
  };

  /* ดาวน์โหลดภาพ ทีละภาพ / ติ๊กหลายภาพเป็น zip (A2 · 25 ต.ค. 2569) · ชื่อไฟล์ = รหัสสินค้า_เลขงาน */
  const dl = useBulkDownload();
  const dlName = productSku || `สินค้า${id}`;
  const dlItem = (row) => ({ id: row.id, url: row.image_url, name: `${dlName}_${row.id}` });
  const dlAll = list.filter((row) => row.image_url).map(dlItem);

  const loadMeta = () => {
    FetchData('GET', `/${mod}`, app.token, {})
      .then((result) => {
        if (result.status === 'ok' && alive.current) setMeta(result);
      })
      .catch(() => {
        if (alive.current) showNotify(t(`alert.loaderror`), 'error');
      });
  };

  const loadList = () => {
    FetchData('OPTIONS', `/${mod}/by_product`, app.token, {
      product_id: id,
      scope: scopeRef.current ? 'all' : 'mine',
      limit: limitRef.current,
      _t: Date.now(),
    })
      .then((result) => {
        if (!alive.current) return;
        /* สลับ "ดูของทุกคน" ระหว่างคำขอค้าง — คำตอบของมุมมองเดิมมาทีหลังห้ามทับรายการ (รีเช็ค 27 ต.ค. 2569) */
        if (result.status === 'ok' && (result.scope === 'all') !== scopeRef.current) return;
        setLoading(false);
        if (result.status !== 'ok') {
          showNotify(t(`alert.loaderror`), 'error');
          return;
        }
        setList(result.data || []);
        setTotal(Number(result.total) || 0);
        setCanViewAll(result.can_view_all === 'Y');
        setProductSku((result.product && result.product.sku) || '');
        if (result.usage) setMeta((m) => ({ ...m, usage: result.usage }));
        const imgs = (result.product && result.product.images) || [];
        setImages(imgs);
        /* เปิดหน้าครั้งแรก — ใส่รูปหลักของสินค้าเป็นรูปอ้างอิงให้เลย งานปกติจะได้ไม่ต้องกดเพิ่ม
           ได้ค่าแผงเดิมคืนมา → คงรูปเดิม แต่เอารูปแกลเลอรีที่ถูกลบไประหว่างนั้นออก */
        if (!refsInit.current) {
          refsInit.current = true;
          if (restored.current) {
            setRefs((cur) =>
              cur.filter((r) => r.t !== 'g' || imgs.some((im) => String(im.id) === String(r.id)))
            );
          } else {
            const main = imgs.find((im) => im.is_main === 'Y') || imgs[0];
            if (main) setRefs([withKey({ t: 'g', id: main.id, src: main.image })]);
          }
        }
      })
      .catch(() => {
        if (alive.current) {
          setLoading(false);
          showNotify(t(`alert.loaderror`), 'error');
        }
      });
  };

  useEffect(() => {
    if (id) {
      const saved = readPanel(stateKey);
      restored.current = Boolean(saved);
      refsInit.current = false;
      setPrompt(saved ? saved.prompt : '');
      setTemplate(saved ? saved.template : '');
      setRefs(saved ? saved.refs.map(withKey) : []);
      setRatio(saved ? saved.ratio : '1:1');
      setResolution(saved ? saved.resolution : '1K');
      setProvider(saved ? saved.provider : 'auto');
      setReadyKey(stateKey);
      loadMeta();
      loadList();
    }
  }, [id]);

  /* จำค่าแผง — หน่วยความจำทันที · sessionStorage หน่วง 0.5 วิ (รูปอัปโหลดใหญ่ ไม่เขียนทุกตัวอักษรที่พิมพ์) */
  useEffect(() => {
    if (!id || readyKey !== stateKey) return undefined;
    const entry = {
      at: Date.now(),
      data: {
        prompt,
        template,
        ratio,
        resolution,
        provider,
        refs: refs.map((r) =>
          r.t === 'u' ? { t: 'u', src: r.src } : { t: r.t, id: r.id, src: r.src }
        ),
      },
    };
    panelMemory.set(stateKey, entry);
    saveTimer.current = setTimeout(() => writePanel(stateKey, entry), 500);
    return () => clearTimeout(saveTimer.current);
  }, [prompt, template, refs, ratio, resolution, provider, readyKey, stateKey]);

  /* ชนิดงานขึ้นกับว่ามีรูปอ้างอิงไหม — มี = แก้จากรูป · ไม่มี = วาดใหม่ */
  const kind = refs.length ? 'IMAGE_EDIT' : 'IMAGE_GEN';
  const providerOptions = meta.providers.filter((p) => p.kind === kind);
  const editProviderOptions = meta.providers.filter((p) => p.kind === 'IMAGE_EDIT');
  /* โมเดลที่เลือกใช้กับชนิดงานใหม่ไม่ได้ → กลับเป็นอัตโนมัติ และบอกผู้ใช้ (เดิมเปลี่ยนเงียบ ๆ · 18 ต.ค. 2569)
     รอรายการโมเดลโหลดก่อน ไม่งั้นโมเดลที่จำไว้ถูกล้างตั้งแต่เปิดแท็บ */
  const prevKind = useRef(kind);
  useEffect(() => {
    const kindChanged = prevKind.current !== kind;
    prevKind.current = kind;
    if (!meta.providers.length || provider === 'auto') return;
    if (!providerOptions.some((p) => p.code === provider)) {
      setProvider('auto');
      let msg = 'โมเดลที่เลือกไว้ใช้ไม่ได้แล้ว เปลี่ยนเป็นอัตโนมัติ';
      if (kindChanged) {
        msg =
          kind === 'IMAGE_EDIT'
            ? 'มีรูปอ้างอิงแล้ว โมเดลเปลี่ยนเป็นอัตโนมัติ'
            : 'ไม่มีรูปอ้างอิงแล้ว โมเดลเปลี่ยนเป็นอัตโนมัติ';
      }
      showNotify(msg, 'info');
    }
  }, [kind, meta.providers]);

  const ratioOptions = meta.ratios.length ? meta.ratios : ['1:1'];
  const resOptions = meta.resolutions.length ? meta.resolutions : ['1K', '2K'];

  const providerPicked =
    provider === 'auto'
      ? providerOptions.find((p) => p.is_default === 'Y') || providerOptions[0]
      : providerOptions.find((p) => p.code === provider);
  const editDefault =
    editProviderOptions.find((p) => p.is_default === 'Y') || editProviderOptions[0];
  const editPicked =
    editProvider === 'auto'
      ? editProviderOptions.find((p) => p.is_default === 'Y')
      : editProviderOptions.find((p) => p.code === editProvider);
  const editCredit = editPicked ? creditOf(editPicked, editResolution) : null;
  const limitProvider =
    provider === 'auto' ? editDefault : editProviderOptions.find((p) => p.code === provider);
  const maxRefs = limitProvider ? Number(limitProvider.max_images) || 14 : 14;
  const credit = providerPicked ? creditOf(providerPicked, resolution) : '';

  /* สิทธิ์และเหตุที่สร้างไม่ได้ — บอกชัดก่อนผู้ใช้กรอก ไม่ใช่ตัวเล็กใต้ปุ่มสีเทา (18 ต.ค. 2569)
     readOnly รอค่าจาก GET ก่อน (ค่าเริ่ม can_generate 'N' ไม่งั้นทุกคนเห็นป้ายแวบตอนเปิดแท็บ) */
  const { usage } = meta;
  const mayGenerate = meta.can_generate === 'Y';
  const readOnly = meta.status === 'ok' && !mayGenerate;
  let blockReason = '';
  if (usage && mayGenerate) {
    if (usage.kill_switch === 'Y') blockReason = AI_ERROR_TEXT.ai_kill_switch_on;
    else if (!(Number(usage.quota) > 0)) blockReason = AI_ERROR_TEXT.ai_quota_not_set;
    else if (
      (usage.over_quota || 'STOP') === 'STOP' &&
      Number(usage.month_images) >= Number(usage.quota)
    ) {
      blockReason = `เดือนนี้ใช้ครบโควตาแล้ว (${usage.month_images}/${usage.quota} ภาพ)`;
    }
  }
  const canGenerate = mayGenerate && !blockReason;
  const usagePct =
    usage && usage.quota > 0
      ? Math.min(100, Math.round((usage.month_images / usage.quota) * 100))
      : 0;
  const lockInput = { disabled: readOnly }; // MDInput ไม่ส่ง disabled ต่อให้ TextField — ปิดที่ตัว input เอง

  /* ── รูปอ้างอิง ─────────────────────────────────────────────── */
  const addRefs = (items) => {
    setRefs((cur) => {
      const room = maxRefs - cur.length;
      if (room <= 0) return cur;
      const fresh = items
        .filter((it) => !(it.t !== 'u' && cur.some((c) => c.t === it.t && c.id === it.id)))
        .slice(0, room)
        .map(withKey);
      return [...cur, ...fresh];
    });
  };
  const removeRef = (key) => setRefs((cur) => cur.filter((r) => r.key !== key));
  const makeMain = (key) => {
    setRefs((cur) => [...cur.filter((r) => r.key === key), ...cur.filter((r) => r.key !== key)]);
    /* ช่องที่กดกลายเป็นรูปหลัก (ไม่ใช่ปุ่มแล้ว) โฟกัสจะหลุดไป body — ย้ายไปปุ่มเอาออกของรูปหลักใหม่ (18 ต.ค. 2569) */
    requestAnimationFrame(() => {
      const panel = document.querySelector('section[aria-label="แผงสร้างภาพสินค้า"]');
      const btn = panel && panel.querySelector('button[aria-label="เอารูปอ้างอิงออก"]');
      if (btn) btn.focus();
    });
  };

  const uploadFiles = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const room = maxRefs - refs.length;
    if (room <= 0) {
      showNotify(`ใส่รูปอ้างอิงได้สูงสุด ${maxRefs} รูปสำหรับโมเดลนี้`, 'warning');
      return;
    }
    if (files.length > room) showNotify(`ใส่เพิ่มได้อีก ${room} รูป ใช้ ${room} รูปแรก`, 'warning');
    files.slice(0, room).forEach((file) => {
      Resizer.imageFileResizer(
        file,
        1024,
        1024,
        'JPEG',
        92,
        0,
        (uri) => addRefs([{ t: 'u', src: uri }]),
        'base64'
      );
    });
  };

  const openGallery = () => {
    setAddAnchor(null);
    setGalleryOpen(true);
  };
  const confirmGallery = (ids) => {
    const room = maxRefs - refs.length;
    if (ids.length > room) {
      showNotify(
        `ใส่เพิ่มได้อีก ${room} รูป (เพดาน ${maxRefs}) ใช้ ${room} รูปแรกที่เลือก`,
        'warning'
      );
    }
    addRefs(
      images
        .filter((im) => ids.includes(String(im.id)))
        .map((im) => ({ t: 'g', id: im.id, src: im.image }))
    );
    setGalleryOpen(false);
  };
  const galleryMain = images.find((im) => im.is_main === 'Y');

  /* ── เทมเพลต ────────────────────────────────────────────────── */
  const templates = [...(meta.presets || [])].sort((a, b) => {
    const ia = TEMPLATE_CAT_ORDER.indexOf(a.category);
    const ib = TEMPLATE_CAT_ORDER.indexOf(b.category);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });
  const templateGroups = templates.reduce((acc, p) => {
    const cat = TEMPLATE_CAT_ORDER.includes(p.category) ? p.category : 'โปสเตอร์ / แบรนด์';
    const g = acc.find((x) => x[0] === cat);
    if (g) g[1].push(p);
    else acc.push([cat, [p]]);
    return acc;
  }, []);
  const activeCat =
    tplCat && templateGroups.some((g) => g[0] === tplCat)
      ? tplCat
      : templateGroups.length
      ? templateGroups[0][0]
      : '';
  const activeTemplates = (templateGroups.find((g) => g[0] === activeCat) || [null, []])[1];
  const templateSel = templates.find((x) => x.code === template && x.prompt === prompt);

  const [confirmBox, setConfirmBox] = useState(null); // กล่องยืนยันแบบเดียวกับหน้าอื่น แทน window.confirm (18 ต.ค. 2569)
  const doApplyTemplate = (p) => {
    setTemplate(p.code);
    setPrompt(p.prompt);
    setTplOpen(false);
    /* เทมเพลตที่ต้องใช้สินค้าจริงแต่ยังไม่มีรูปอ้างอิง → ใส่รูปหลักให้ */
    if (!refs.length && p.prompt.includes('รูปที่แนบ')) {
      const main = images.find((im) => im.is_main === 'Y') || images[0];
      if (main) {
        addRefs([{ t: 'g', id: main.id, src: main.image }]);
        showNotify('เทมเพลตนี้ใช้สินค้าจริง ใส่รูปหลักเป็นรูปอ้างอิงให้แล้ว', 'info');
      }
    }
  };
  const applyTemplate = (p) => {
    const edited =
      prompt.trim() !== '' && !templates.some((x) => x.prompt.trim() === prompt.trim());
    if (edited) {
      setConfirmBox({
        title: 'แทนที่คำสั่งที่พิมพ์ไว้?',
        text: 'คำสั่งในช่องจะถูกแทนด้วยคำสั่งของเทมเพลตนี้',
        okLabel: 'แทนที่',
        onOk: () => doApplyTemplate(p),
      });
      return;
    }
    doApplyTemplate(p);
  };

  /* ── poll งานที่ยังไม่จบ (ทุก 6 วิ · preventDuplicate 5 วิ) ─────────── */
  /* ป้าย "กำลังสร้าง" นับเฉพาะงานของตัวเอง — เปิดดูของทุกคนแล้วงานค้างของคนอื่นไม่เกี่ยวกับเรา (รีเช็ค 27 ต.ค. 2569) */
  const hasPending = list.some(
    (g) =>
      (g.status === 'PENDING' || g.status === 'RUNNING') &&
      String(g.created_by) === String(app.user.id)
  );
  /* งานสำเร็จแต่ยังไม่มีไฟล์ (ดาวน์โหลดพลาด) poll ต่อจนได้ภาพ — worker โหลดซ้ำทุกนาที (16 ต.ค. 2569)
     จำกัดรอบต่อภาพ กันยิงไม่รู้จบเมื่อลิงก์ชั่วคราวของ kie.ai หมดอายุแล้ว */
  const needsPoll = (g) =>
    g.status === 'PENDING' ||
    g.status === 'RUNNING' ||
    (g.status === 'SUCCESS' && !g.image_url && (fetchTries.current[g.id] || 0) < MAX_FETCH_TRIES);
  const hasPolling = list.some(needsPoll);
  useEffect(() => {
    if (!hasPolling) return undefined;
    const timer = setInterval(() => {
      const pending = list.filter(needsPoll);
      if (!pending.length) return;
      pending.forEach((g) => {
        if (g.status === 'SUCCESS') fetchTries.current[g.id] = (fetchTries.current[g.id] || 0) + 1;
      });
      /* รอบสุดท้ายของงานที่ยังไม่ได้ไฟล์ — จบรอบแล้วขึ้น "โหลดภาพไม่ได้" แทนวงหมุนค้าง (18 ต.ค. 2569) */
      const lastTry = pending.filter(
        (g) => g.status === 'SUCCESS' && fetchTries.current[g.id] >= MAX_FETCH_TRIES
      );
      const markLost = (rows) => {
        if (rows.length) {
          setLostIds((cur) => [...cur, ...rows.map((g) => g.id).filter((x) => !cur.includes(x))]);
        }
      };
      Promise.all(
        pending.map((g) =>
          FetchData('OPTIONS', `/${mod}/poll`, app.token, { id: g.id, _t: Date.now() })
        )
      )
        .then((results) => {
          if (!alive.current) return;
          const changed = results.some((r, i) =>
            pending[i].status === 'SUCCESS'
              ? Boolean(r.image_url)
              : r.state === 'SUCCESS' || r.state === 'FAILED'
          );
          markLost(lastTry.filter((g) => !results[pending.indexOf(g)].image_url));
          if (changed) loadList();
        })
        .catch(() => {
          // เน็ตสะดุด — รอบถัดไป poll ใหม่เอง ไม่ขึ้นแจ้งเตือนทุก 6 วิ
          if (alive.current) markLost(lastTry);
        });
    }, 6000);
    return () => clearInterval(timer);
  }, [hasPolling, list]);

  /* ── สั่งสร้าง ──────────────────────────────────────────────── */
  const generate = () => {
    if (generating.current || !canGenerate) return;
    if (refs.length > maxRefs) {
      showNotify(`รูปอ้างอิงเกินที่โมเดลนี้รับได้ (สูงสุด ${maxRefs} รูป)`, 'warning');
      return;
    }
    if (promptLength(prompt) > 2000) {
      showNotify('คำสั่งเกิน 2,000 ตัวอักษร ลดข้อความก่อนสร้าง', 'warning');
      return;
    }
    if (!prompt.trim()) {
      showNotify('พิมพ์คำสั่ง หรือกด "เทมเพลต" ก่อน', 'warning');
      return;
    }
    generating.current = true;
    setLoading(true);
    FetchData('PUT', `/${mod}`, app.token, {
      product_id: id,
      prompt: prompt,
      resolution: resolution,
      aspect_ratio: ratio,
      source: refs.length ? 'refs' : 'none',
      refs: refs.map((r) => (r.t === 'u' ? { t: 'u', d: r.src } : { t: r.t, id: r.id })),
      provider_code: provider === 'auto' ? '' : provider,
      _t: Date.now(),
    })
      .then((result) => {
        generating.current = false;
        if (!alive.current) return;
        setLoading(false);
        if (result.status === 'ok') {
          showNotify('ส่งงานแล้ว ภาพจะขึ้นในผลงานภายใน 2–5 นาที', 'success');
          clearTimeout(saveTimer.current);
          clearPanel(stateKey);
          loadList();
        } else if (result.msg === 'ai_attach_too_many') {
          showNotify(`รูปอ้างอิงเกินที่โมเดลนี้รับได้ (สูงสุด ${maxRefs} รูป)`, 'error');
        } else {
          showNotify(AI_ERROR_TEXT[result.msg] || t(`alert.saveerror`), 'error');
        }
      })
      .catch(() => {
        generating.current = false;
        if (alive.current) {
          setLoading(false);
          showNotify('ส่งงานไม่สำเร็จ · คำสั่งยังอยู่ในแผง ตรวจผลงานแล้วกดสร้างอีกครั้ง', 'error');
        }
      });
  };

  const saveEdit = () => {
    if (promptLength(editPrompt) > 2000) {
      showNotify('คำสั่งแก้ภาพเกิน 2,000 ตัวอักษร ลดข้อความก่อนสร้าง', 'warning');
      return;
    }
    if (!editRow || !editPrompt.trim() || loading || !editPicked || !canGenerate) return;
    setLoading(true);
    FetchData('PATCH', `/${mod}/${editRow.id}`, app.token, {
      prompt: editPrompt,
      resolution: editResolution,
      aspect_ratio: editRatio,
      provider_code: editPicked.code,
      _t: Date.now(),
    })
      .then((result) => {
        if (!alive.current) return;
        setLoading(false);
        if (result.status === 'ok') {
          setEditRow(null);
          showNotify('ส่งงานแก้ภาพแล้ว ภาพจะขึ้นในผลงานภายใน 2–5 นาที', 'success');
          loadList();
        } else {
          showNotify(AI_ERROR_TEXT[result.msg] || t(`alert.saveerror`), 'error');
        }
      })
      .catch(() => {
        if (alive.current) {
          setLoading(false);
          showNotify(
            'ส่งงานแก้ภาพไม่สำเร็จ · คำสั่งยังอยู่ในแผง ตรวจผลงานแล้วกดสร้างอีกครั้ง',
            'error'
          );
        }
      });
  };

  const approve = (row) => {
    if (approvingIds.current.has(row.id)) return;
    approvingIds.current.add(row.id);
    setApproving([...approvingIds.current]);
    FetchData('OPTIONS', `/${mod}/approve`, app.token, { id: row.id, _t: Date.now() })
      .then((result) => {
        approvingIds.current.delete(row.id);
        if (!alive.current) return;
        setApproving([...approvingIds.current]);
        if (result.status === 'ok') {
          setGalleryMessage(
            result.primary_image_set === 'Y'
              ? 'เก็บเข้าแกลเลอรีแล้ว · ตั้งเป็นรูปหลักเพราะสินค้านี้ยังไม่มีรูปหลัก'
              : result.primary_image_set === 'N'
              ? 'เก็บเข้าแกลเลอรีแล้ว · รูปหลักเดิมยังอยู่'
              : 'เก็บเข้าแกลเลอรีแล้ว · ตรวจรูปหลักได้ที่ เปิดแกลเลอรีสินค้า'
          );
          setList((items) =>
            items.map((item) => (item.id === row.id ? { ...item, approved: 'Y' } : item))
          );
          showNotify('เก็บเข้าแกลเลอรีแล้ว', 'success');
          loadList();
        } else if (result.msg === 'ai_already_approved') {
          showNotify('ภาพนี้อยู่ในแกลเลอรีแล้ว', 'warning');
        } else {
          showNotify(AI_ERROR_TEXT[result.msg] || 'เก็บเข้าแกลเลอรีไม่สำเร็จ', 'error');
        }
      })
      .catch(() => {
        approvingIds.current.delete(row.id);
        if (alive.current) {
          setApproving([...approvingIds.current]);
          showNotify('เก็บเข้าแกลเลอรีไม่สำเร็จ ตรวจแกลเลอรีก่อนลองใหม่', 'error');
        }
      });
  };

  const remove = () => {
    if (!delRow) return;
    setLoading(true);
    FetchData('DELETE', `/${mod}/${delRow.id}`, app.token, {})
      .then((result) => {
        if (!alive.current) return;
        setLoading(false);
        setDelRow(null);
        if (result.status === 'ok') {
          showNotify(t(`alert.deletecomplete`), 'success');
          loadList();
        } else {
          showNotify(AI_ERROR_TEXT[result.msg] || t(`alert.deleteerror`), 'error');
        }
      })
      .catch(() => {
        if (alive.current) {
          setLoading(false);
          showNotify(t(`alert.deleteerror`), 'error');
        }
      });
  };

  /* "ลองใหม่" ของงานที่ไม่สำเร็จ — ใส่คำสั่ง/สัดส่วน/ความละเอียดเดิมกลับในแผง ไม่สั่งสร้างเอง (18 ต.ค. 2569) */
  const doRetry = (row) => {
    const text = row.prompt || '';
    const tpl = templates.find((x) => x.prompt === text);
    setTemplate(tpl ? tpl.code : '');
    setPrompt(text);
    if (ratioOptions.includes(row.aspect_ratio)) setRatio(row.aspect_ratio);
    if (resOptions.includes(row.resolution)) setResolution(row.resolution);
    if (promptRef.current) promptRef.current.focus();
    showNotify('ใส่คำสั่งเดิมในแผงแล้ว กดสร้างภาพเพื่อลองใหม่', 'info');
  };
  const retry = (row) => {
    const text = row.prompt || '';
    if (prompt.trim() !== '' && prompt.trim() !== text.trim()) {
      setConfirmBox({
        title: 'แทนที่คำสั่งที่พิมพ์ไว้?',
        text: 'คำสั่งในช่องจะถูกแทนด้วยคำสั่งของงานนี้',
        okLabel: 'แทนที่',
        onOk: () => doRetry(row),
      });
      return;
    }
    doRetry(row);
  };

  /* dialog แก้ภาพเริ่มจากสัดส่วน/ความละเอียดของภาพนั้น ไม่ใช่ของแผงซ้าย (18 ต.ค. 2569) */
  const openEdit = (row) => {
    setEditRow(row);
    setEditPrompt('');
    setEditProvider('auto');
    setEditRatio(ratioOptions.includes(row.aspect_ratio) ? row.aspect_ratio : ratio);
    setEditResolution(resOptions.includes(row.resolution) ? row.resolution : resolution);
  };

  /* "ใช้ภาพนี้เป็นรูปอ้างอิง" จากผลงาน — ต่อยอดได้ทันที (เป็นรูปอัปโหลดชั่วคราว) */
  /* ส่ง id ของผลงานให้ backend ดึงไฟล์เอง (t:'a') — เดิม fetch รูปจาก browser ติด CORS เมื่อรูปอยู่คนละ origin (18 ต.ค. 2569) */
  const useAsRef = (row) => {
    if (!row.image_url) return;
    if (refs.some((r) => r.t === 'a' && r.id === row.id)) {
      showNotify('รูปนี้เป็นรูปอ้างอิงอยู่แล้ว', 'info');
      return;
    }
    if (refs.length >= maxRefs) {
      showNotify(`รูปอ้างอิงเต็มแล้ว (${maxRefs} รูป)`, 'warning');
      return;
    }
    addRefs([{ t: 'a', id: row.id, src: row.image_url }]);
    showNotify('ใส่เป็นรูปอ้างอิงแล้ว', 'info');
  };

  const tileSx = {
    width: 62,
    height: 62,
    borderRadius: 2,
    flexShrink: 0,
    position: 'relative',
    overflow: 'hidden',
  };

  return (
    <MDBox pt={1}>
      <Grid container spacing={3} alignItems='flex-start'>
        {/* ════════════════ แผงสั่งงาน (ซ้าย) ════════════════ */}
        {/* item ต้องยืดเต็มแถว แผง sticky ข้างในถึงเลื่อนตามได้ (สูงเท่าแผงพอดี = ไม่ติดเลย) */}
        <Grid item xs={12} md={5} lg={4} sx={{ alignSelf: { md: 'stretch' } }}>
          <MDBox
            component='section'
            aria-label='แผงสร้างภาพสินค้า'
            sx={{
              /* top 96 พ้นแถบบน · สูงไม่เกินจอ เลื่อนในแผง · ปุ่มสร้างติดท้ายแผง — แบบหน้ากราฟิกแคมเปญ (18 ต.ค. 2569) */
              position: { md: 'sticky' },
              top: { md: 96 },
              maxHeight: { md: 'calc(100vh - 112px)' },
              overflow: { xs: 'hidden', md: 'auto' },
              border: '1px solid',
              borderColor: 'grey.300',
              borderRadius: 3,
              bgcolor: '#fff',
            }}
          >
            {/* หัวแผง + ปุ่มเทมเพลต */}
            <MDBox
              px={2.5}
              py={2}
              display='flex'
              alignItems='center'
              justifyContent='space-between'
              gap={1}
              sx={{ bgcolor: '#F4F7FD', borderBottom: '1px solid', borderColor: 'grey.200' }}
            >
              <MDBox minWidth={0}>
                <MDTypography variant='caption' color='text' sx={{ opacity: 0.7 }}>
                  เครื่องมือ AI
                </MDTypography>
                <MDTypography variant='h6' fontWeight='bold' lineHeight={1.2}>
                  สร้างภาพสินค้า
                </MDTypography>
              </MDBox>
              {!readOnly && (
                <MDButton
                  variant='outlined'
                  color='info'
                  size='small'
                  onClick={() => setTplOpen(true)}
                >
                  <Icon>dashboard_customize</Icon>&nbsp;เทมเพลต
                </MDButton>
              )}
            </MDBox>

            {readOnly && (
              <MDBox
                mx={2.5}
                mt={2}
                px={1.5}
                py={1.25}
                display='flex'
                gap={1}
                alignItems='flex-start'
                role='status'
                sx={{ borderRadius: 2, bgcolor: 'rgba(251,140,0,0.12)', color: '#9A4D00', mb: 2.5 }}
              >
                <Icon fontSize='small' sx={{ mt: '2px' }}>
                  visibility
                </Icon>
                <MDTypography
                  variant='button'
                  fontWeight='medium'
                  color='inherit'
                  lineHeight={1.45}
                >
                  บัญชีนี้ยังสร้างภาพ AI ไม่ได้ · ขอสิทธิ์จากผู้ดูแลระบบ
                </MDTypography>
              </MDBox>
            )}

            {/* บัญชีดูอย่างเดียว: พับฟอร์มและปุ่มสร้าง เหลือป้ายแจ้ง (เหมือนหน้ากราฟิกแคมเปญ · 18 ต.ค. 2569) */}
            <MDBox
              px={2.5}
              pt={2}
              pb={2.5}
              flexDirection='column'
              gap={2.25}
              sx={{ display: readOnly ? 'none' : 'flex' }}
            >
              {/* โมเดล */}
              <MDBox>
                <Label>โมเดล</Label>
                <MDInput
                  fullWidth
                  select
                  value={provider}
                  disabled={readOnly}
                  InputProps={lockInput}
                  onChange={(e) => setProvider(e.target.value)}
                >
                  <MenuItem value='auto'>✦ อัตโนมัติ (แนะนำ)</MenuItem>
                  {providerOptions.map((p) => (
                    <MenuItem key={p.code} value={p.code}>
                      {p.name} · {creditOf(p, resolution)} เครดิต
                    </MenuItem>
                  ))}
                </MDInput>
              </MDBox>

              {/* รูปอ้างอิง */}
              <MDBox>
                <Label right={`${refs.length}/${maxRefs}`}>รูปอ้างอิง</Label>
                <MDBox display='flex' gap={1} flexWrap='wrap'>
                  {refs.map((r, i) => (
                    <MDBox
                      key={r.key}
                      sx={{
                        ...tileSx,
                        border: '2px solid',
                        borderColor: i === 0 ? 'info.main' : 'grey.300',
                        bgcolor: '#fff',
                      }}
                    >
                      {i === 0 || readOnly ? (
                        <img
                          src={r.src}
                          alt={i === 0 ? 'รูปหลัก' : `รูปอ้างอิง ${i + 1}`}
                          title={i === 0 ? 'รูปหลัก' : ''}
                          onError={onImgError}
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                      ) : (
                        <ButtonBase
                          focusRipple
                          onClick={() => makeMain(r.key)}
                          aria-label={`ตั้งรูปอ้างอิง ${i + 1} เป็นรูปหลัก`}
                          title='กดเพื่อตั้งเป็นรูปหลัก'
                          sx={{ width: '100%', height: '100%', ...focusSx }}
                        >
                          <img
                            src={r.src}
                            alt=''
                            onError={onImgError}
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          />
                        </ButtonBase>
                      )}
                      {!readOnly && (
                        <ButtonBase
                          focusRipple
                          onClick={() => removeRef(r.key)}
                          aria-label='เอารูปอ้างอิงออก'
                          title='เอารูปอ้างอิงออก'
                          sx={{
                            position: 'absolute',
                            top: 3,
                            right: 3,
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            bgcolor: 'rgba(0,0,0,0.6)',
                            color: '#fff',
                            ...focusSx,
                          }}
                        >
                          <Icon sx={{ fontSize: '13px !important' }}>close</Icon>
                        </ButtonBase>
                      )}
                      {i === 0 && (
                        <MDBox
                          sx={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            bgcolor: 'info.main',
                            color: '#fff',
                            fontSize: 10,
                            textAlign: 'center',
                            pointerEvents: 'none',
                          }}
                        >
                          หลัก
                        </MDBox>
                      )}
                    </MDBox>
                  ))}
                  {refs.length < maxRefs && !readOnly && (
                    <ButtonBase
                      focusRipple
                      onClick={(e) => setAddAnchor(e.currentTarget)}
                      aria-label='เพิ่มรูปอ้างอิง'
                      aria-haspopup='menu'
                      sx={{
                        ...tileSx,
                        border: '1.5px dashed',
                        borderColor: 'grey.400',
                        bgcolor: 'grey.100',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'text.secondary',
                        '&:hover': { borderColor: 'info.main', color: 'info.main' },
                        ...focusSx,
                      }}
                    >
                      <Icon>add</Icon>
                      <MDTypography variant='caption' color='inherit'>
                        เพิ่ม
                      </MDTypography>
                    </ButtonBase>
                  )}
                </MDBox>
                {!refs.length && !readOnly && (
                  <MDTypography
                    variant='caption'
                    color='text'
                    display='block'
                    mt={0.75}
                    sx={{ opacity: 0.7 }}
                  >
                    ไม่ใส่รูป = AI วาดสินค้าเอง อาจไม่เหมือนของจริง
                  </MDTypography>
                )}
                {refs.length > maxRefs && (
                  <MDTypography variant='caption' color='error' display='block' mt={0.75}>
                    โมเดลนี้รับได้ {maxRefs} รูป เอาออก {refs.length - maxRefs} รูป
                  </MDTypography>
                )}
                <input
                  ref={fileRef}
                  type='file'
                  multiple
                  accept='image/jpeg,image/png,image/webp'
                  style={{ display: 'none' }}
                  onChange={uploadFiles}
                />
                <Menu
                  anchorEl={addAnchor}
                  open={Boolean(addAnchor)}
                  onClose={() => setAddAnchor(null)}
                >
                  <MenuItem onClick={openGallery} disabled={!images.length}>
                    <ListItemIcon>
                      <Icon fontSize='small'>photo_library</Icon>
                    </ListItemIcon>
                    จากแกลเลอรีสินค้า ({images.length})
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setAddAnchor(null);
                      if (fileRef.current) fileRef.current.click();
                    }}
                  >
                    <ListItemIcon>
                      <Icon fontSize='small'>upload</Icon>
                    </ListItemIcon>
                    อัปโหลดจากเครื่อง (เลือกได้หลายรูป)
                  </MenuItem>
                </Menu>
              </MDBox>

              {/* คำสั่ง */}
              <MDBox>
                <Label>คำสั่ง</Label>
                {templateSel && (
                  <Chip
                    size='small'
                    color='info'
                    variant='outlined'
                    label={`เทมเพลต: ${templateSel.name}`}
                    disabled={readOnly}
                    onDelete={() => {
                      setTemplate('');
                      setPrompt('');
                    }}
                    sx={{ mb: 1 }}
                  />
                )}
                <MDInput
                  fullWidth
                  multiline
                  rows={5}
                  inputRef={promptRef}
                  placeholder='บอกว่าอยากได้ภาพแบบไหน เช่น พื้นหลังขาว แสงสตูดิโอ — หรือกด "เทมเพลต" ด้านบน'
                  value={prompt}
                  disabled={readOnly}
                  InputProps={lockInput}
                  onChange={(e) => setPrompt(e.target.value)}
                  error={promptLength(prompt) > 2000}
                  helperText={
                    promptLength(prompt) > 1800
                      ? `${promptLength(prompt)}/2000 ตัวอักษร${
                          promptLength(prompt) > 2000 ? ' — ลดข้อความให้สั้นลง' : ''
                        }`
                      : ''
                  }
                />
                <MDBox mt={1} display='flex' gap={0.75} flexWrap='wrap' alignItems='center'>
                  <MDTypography variant='caption' color='text' sx={{ opacity: 0.7 }}>
                    ลองใช้:
                  </MDTypography>
                  {templates.slice(0, QUICK_COUNT).map((p) => (
                    <Chip
                      key={p.code}
                      size='small'
                      clickable
                      label={p.name.replace(/\s*\(.*\)\s*$/, '')}
                      color={templateSel && templateSel.code === p.code ? 'info' : 'default'}
                      variant={templateSel && templateSel.code === p.code ? 'filled' : 'outlined'}
                      disabled={readOnly}
                      onClick={() => applyTemplate(p)}
                    />
                  ))}
                </MDBox>
              </MDBox>

              {/* สัดส่วน + ความละเอียด */}
              <MDBox display='flex' gap={1.5}>
                <MDBox flex={1}>
                  <Label>สัดส่วน</Label>
                  <MDInput
                    fullWidth
                    select
                    value={ratio}
                    disabled={readOnly}
                    InputProps={lockInput}
                    onChange={(e) => setRatio(e.target.value)}
                  >
                    {ratioOptions.map((r) => (
                      <MenuItem key={r} value={r}>
                        {r}
                        {RATIO_LABELS[r] ? ` · ${RATIO_LABELS[r]}` : ''}
                      </MenuItem>
                    ))}
                  </MDInput>
                </MDBox>
                <MDBox flex={1}>
                  <Label>ความละเอียด</Label>
                  <MDInput
                    fullWidth
                    select
                    value={resolution}
                    disabled={readOnly}
                    InputProps={lockInput}
                    onChange={(e) => setResolution(e.target.value)}
                  >
                    {resOptions.map((r) => (
                      <MenuItem key={r} value={r}>
                        {r}
                      </MenuItem>
                    ))}
                  </MDInput>
                </MDBox>
              </MDBox>
            </MDBox>

            {/* ปุ่มสร้าง — ติดท้ายแผงตลอด แม้แผงต้องเลื่อน */}
            <MDBox
              hidden={readOnly}
              px={2.5}
              pt={1.5}
              pb={2.5}
              sx={{
                position: { md: 'sticky' },
                bottom: 0,
                zIndex: 1,
                bgcolor: '#fff',
                borderTop: '1px solid',
                borderColor: 'grey.200',
              }}
            >
              {blockReason && (
                <MDBox display='flex' gap={0.75} alignItems='flex-start' mb={1} role='status'>
                  <Icon color='error' fontSize='small' sx={{ mt: '1px' }}>
                    block
                  </Icon>
                  <MDTypography variant='caption' color='error' fontWeight='medium'>
                    {blockReason}
                  </MDTypography>
                </MDBox>
              )}
              <MDButton
                variant='gradient'
                color='info'
                fullWidth
                disabled={
                  !canGenerate || loading || refs.length > maxRefs || promptLength(prompt) > 2000
                }
                onClick={generate}
                sx={{ py: 1.5, fontSize: '0.95rem', justifyContent: 'space-between' }}
              >
                <span>สร้างภาพ{credit !== '' ? ` · ${credit} เครดิต` : ''}</span>
                <Icon>auto_awesome</Icon>
              </MDButton>
              {usage && (
                <MDBox mt={1.25}>
                  {/* MDProgress ไม่ใช่ LinearProgress — theme บังคับ transform ของแถบเป็น 0 แถบ MUI จึงเต็มเสมอ (Codex ข้อ 8) */}
                  <MDProgress value={usagePct} color={usagePct >= 80 ? 'warning' : 'info'} />
                  <MDTypography
                    variant='caption'
                    color='text'
                    display='block'
                    mt={0.5}
                    sx={{ opacity: 0.75 }}
                  >
                    เดือนนี้ทั้งร้าน {usage.month_images}
                    {usage.quota > 0 ? `/${usage.quota}` : ''} ภาพ
                    {usage.my_images != null ? ` · คุณ ${usage.my_images}` : ''}
                  </MDTypography>
                </MDBox>
              )}
            </MDBox>
          </MDBox>
        </Grid>

        {/* ════════════════ ผลงาน (ขวา) ════════════════ */}
        <Grid item xs={12} md={7} lg={8}>
          <MDBox display='flex' alignItems='baseline' justifyContent='space-between' mb={1.5}>
            <MDTypography variant='h6' fontWeight='medium'>
              ผลงาน{' '}
              <MDTypography component='span' variant='caption' color='text' sx={{ opacity: 0.6 }}>
                {list.length ? `${list.length} รายการ` : ''}
              </MDTypography>
            </MDTypography>
            <MDBox display='flex' alignItems='center' gap={1} flexWrap='wrap'>
              {canViewAll && (
                <FormControlLabel
                  sx={{ mr: 0 }}
                  control={
                    <Switch
                      size='small'
                      checked={scopeAll}
                      onChange={(e) => {
                        scopeRef.current = e.target.checked;
                        setScopeAll(e.target.checked);
                        dl.clear();
                        setLoading(true);
                        loadList();
                      }}
                    />
                  }
                  label={<MDTypography variant='caption'>ดูของทุกคน</MDTypography>}
                />
              )}
              {hasPending && (
                <Chip
                  size='small'
                  color='warning'
                  icon={<CircularProgress size={12} color='inherit' />}
                  label='กำลังสร้าง'
                />
              )}
              <SelectBar
                on={dl.on}
                onToggle={dl.toggleMode}
                selected={dl.count}
                total={dlAll.length}
                onAll={() => dl.addAll(dlAll)}
                onNone={dl.clear}
                onDownload={() => dl.run(`${dlName}_ภาพAI`, showNotify)}
                busy={dl.busy}
                progress={dl.progress}
              />
            </MDBox>
          </MDBox>
          <MDBox mb={2}>
            {onOpenGallery && (
              <GalleryLink productId={id} token={app.token} onOpen={onOpenGallery} />
            )}
            {galleryMessage && (
              <MDTypography variant='caption' display='block' aria-live='polite'>
                {galleryMessage}
              </MDTypography>
            )}
          </MDBox>
          {list.length === 0 ? (
            <MDBox
              py={7}
              px={3}
              display='flex'
              flexDirection='column'
              alignItems='center'
              textAlign='center'
              sx={{
                border: '1px dashed',
                borderColor: 'grey.400',
                borderRadius: 3,
                bgcolor: '#FAFBFD',
              }}
            >
              <MDBox
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '16px',
                  bgcolor: 'rgba(26,115,232,0.1)',
                  color: 'info.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1.5,
                }}
              >
                <Icon fontSize='medium'>add_photo_alternate</Icon>
              </MDBox>
              <MDTypography variant='h6'>
                {scopeAll
                  ? 'สินค้านี้ยังไม่มีภาพ AI'
                  : mayGenerate
                  ? 'คุณยังไม่ได้สร้างภาพ AI ของสินค้านี้'
                  : canViewAll
                  ? 'คุณยังไม่ได้สร้างภาพ AI ของสินค้านี้ — เปิด "ดูของทุกคน" เพื่อดูภาพของคนอื่น'
                  : 'บัญชีนี้เห็นเฉพาะภาพที่ตัวเองสร้าง — ขอสิทธิ์ "ดู/ตรวจรับงาน AI ของทุกคน" จากผู้ดูแลระบบเพื่อดูภาพของคนอื่น'}
              </MDTypography>
              {mayGenerate && (
                <MDTypography variant='body2' color='text' sx={{ opacity: 0.75, maxWidth: 420 }}>
                  เลือกชิป &quot;ลองใช้&quot; หรือ &quot;เทมเพลต&quot; แล้วกด &quot;สร้างภาพ&quot;
                </MDTypography>
              )}
            </MDBox>
          ) : (
            <Grid container spacing={2}>
              {list.map((row) => {
                const working = row.status === 'PENDING' || row.status === 'RUNNING';
                const done = row.status === 'SUCCESS';
                const saved = row.approved === 'Y';
                const hasImage = Boolean(row.image_url);
                /* สำเร็จแต่ยังไม่มีไฟล์ = กำลังดึงภาพ (poll ต่อ) · ครบรอบแล้วยังไม่ได้ = โหลดภาพไม่ได้
                   เดิมขึ้นไอคอนภาพแตกพร้อมปุ่มที่กดแล้ว error (18 ต.ค. 2569) */
                const lost = done && !hasImage && lostIds.includes(row.id);
                const fetching = done && !hasImage && !lost;
                const failed = !working && !done;
                const failReason = row.status === 'FAILED' ? failText(row.error_msg) : '';
                /* ป้ายบนรูปขึ้นเฉพาะสถานะที่ต้องรู้ — ภาพสำเร็จที่ยังไม่เก็บไม่ต้องมีป้าย (18 ต.ค. 2569) */
                let badge = null;
                if (saved) badge = <Chip size='small' color='success' label='อยู่ในแกลเลอรีแล้ว' />;
                else if (!working && !done) {
                  const st = statusList[row.status] || { name: row.status, color: 'default' };
                  badge = (
                    <Chip
                      size='small'
                      color={st.color === 'default' ? undefined : st.color}
                      label={st.name}
                    />
                  );
                }
                return (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    key={row.id}
                    sx={{
                      '@media (min-width: 1536px)': { flexBasis: '33.3333%', maxWidth: '33.3333%' },
                    }}
                  >
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        border: '1px solid',
                        borderColor: 'grey.300',
                        boxShadow: 'none',
                        overflow: 'hidden',
                        borderRadius: 3,
                      }}
                    >
                      <ReviewThumb
                        onReview={row.image_url ? () => setReviewId(row.id) : null}
                        label={`ตรวจภาพงาน ${row.id}`}
                        select={
                          hasImage ? dl.selectProps(dlItem(row), `เลือกภาพงาน ${row.id}`) : null
                        }
                        badge={badge}
                        meta={[
                          row.image_url ? row.resolution : '',
                          row.image_url && row.credits_used > 0 ? `${row.credits_used} เครดิต` : '',
                          scopeAll ? row.created_by_name || 'ระบบ' : '',
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                        sx={{
                          aspectRatio: '1 / 1',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'grey.100',
                        }}
                      >
                        {hasImage ? (
                          <img
                            src={row.image_url}
                            alt={`ai-${row.id}`}
                            title={row.prompt}
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          />
                        ) : working || fetching ? (
                          <MDBox display='flex' flexDirection='column' alignItems='center' gap={1}>
                            <CircularProgress size={28} color='inherit' sx={{ opacity: 0.4 }} />
                            <MDTypography variant='caption' color='text' sx={{ opacity: 0.6 }}>
                              {working ? 'กำลังสร้าง…' : 'กำลังดึงภาพ…'}
                            </MDTypography>
                          </MDBox>
                        ) : (
                          <MDBox
                            display='flex'
                            flexDirection='column'
                            alignItems='center'
                            gap={0.75}
                            px={2}
                            textAlign='center'
                            title={failed && row.error_msg ? row.error_msg : undefined}
                          >
                            <Icon
                              color={failReason ? 'error' : 'inherit'}
                              sx={{ fontSize: '36px !important', opacity: failReason ? 0.8 : 0.3 }}
                            >
                              {lost ? 'image_not_supported' : 'error_outline'}
                            </Icon>
                            {(lost || failReason) && (
                              <MDTypography
                                variant='caption'
                                color={failReason ? 'error' : 'text'}
                                fontWeight='medium'
                                lineHeight={1.35}
                              >
                                {lost ? 'โหลดภาพไม่ได้' : failReason}
                              </MDTypography>
                            )}
                          </MDBox>
                        )}
                      </ReviewThumb>
                      {!working && (
                        <MDBox p={1.25} display='flex' flexDirection='column' flexGrow={1}>
                          <ActionRow>
                            {(failed || lost) && mayGenerate ? (
                              <MDButton
                                size='small'
                                variant='gradient'
                                color='info'
                                onClick={() => retry(row)}
                                aria-label={`ลองใหม่ด้วยคำสั่งของงาน ${row.id}`}
                                data-primary=''
                                sx={{ flexGrow: 1, minWidth: 0, px: 1 }}
                              >
                                <Icon>refresh</Icon>&nbsp;ลองใหม่
                              </MDButton>
                            ) : done && !saved && mayGenerate ? (
                              <MDButton
                                size='small'
                                variant='gradient'
                                color='info'
                                onClick={() => approve(row)}
                                disabled={approving.includes(row.id) || !hasImage}
                                data-primary=''
                                sx={{ flexGrow: 1, minWidth: 0, px: 1 }}
                              >
                                <Icon>collections</Icon>&nbsp;
                                {approving.includes(row.id) ? 'กำลังเก็บ…' : 'เก็บเข้าแกลเลอรี'}
                              </MDButton>
                            ) : saved && onOpenGallery ? (
                              <GalleryLink
                                productId={id}
                                token={app.token}
                                onOpen={onOpenGallery}
                                fullWidth
                              />
                            ) : (
                              <MDBox flexGrow={1} />
                            )}
                            {done && !lost && mayGenerate && (
                              <MDButton
                                size='small'
                                variant='outlined'
                                color='dark'
                                iconOnly
                                title='แก้ภาพนี้ (สร้างรอบใหม่)'
                                aria-label={`แก้ภาพงาน ${row.id}`}
                                sx={iconBtnSx}
                                disabled={!canGenerate || !hasImage}
                                onClick={() => openEdit(row)}
                              >
                                <Icon>edit</Icon>
                              </MDButton>
                            )}
                            {hasImage && (
                              <DownloadButton
                                label={`ดาวน์โหลดภาพงาน ${row.id}`}
                                onClick={() => dl.one(row.image_url, dlItem(row).name, showNotify)}
                              />
                            )}
                            <CardMenu
                              label={`ตัวเลือกเพิ่มเติมของงาน ${row.id}`}
                              items={[
                                done &&
                                  hasImage &&
                                  mayGenerate && {
                                    label: 'ใช้เป็นรูปอ้างอิง',
                                    icon: 'add_to_photos',
                                    onClick: () => useAsRef(row),
                                  },
                                mayGenerate && {
                                  label: 'ลบ',
                                  icon: 'delete_outline',
                                  danger: true,
                                  onClick: () => setDelRow(row),
                                },
                              ]}
                            />
                          </ActionRow>
                        </MDBox>
                      )}
                    </Card>
                  </Grid>
                );
              })}
              {total > list.length ? (
                <Grid item xs={12}>
                  <MDBox textAlign='center'>
                    <MDButton
                      variant='text'
                      color='info'
                      onClick={() => {
                        limitRef.current += 50;
                        loadList();
                      }}
                    >
                      แสดงเพิ่ม · {list.length} จาก {total}
                    </MDButton>
                  </MDBox>
                </Grid>
              ) : null}
            </Grid>
          )}
        </Grid>
      </Grid>

      {/* ── dialog เทมเพลต ───────────────────────────────────────────── */}
      <Dialog open={tplOpen} onClose={() => setTplOpen(false)} fullWidth maxWidth='md'>
        <DialogTitle>เลือกเทมเพลต</DialogTitle>
        <DialogContent>
          <MDBox display='flex' gap={0.75} flexWrap='wrap' mb={2}>
            {templateGroups.map(([cat, items]) => (
              <Chip
                key={cat}
                clickable
                label={`${cat} · ${items.length}`}
                color={cat === activeCat ? 'info' : 'default'}
                variant={cat === activeCat ? 'filled' : 'outlined'}
                onClick={() => setTplCat(cat)}
              />
            ))}
          </MDBox>
          <MDBox
            display='grid'
            gap={1.5}
            sx={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}
          >
            {activeTemplates.map((p) => {
              const on = templateSel && templateSel.code === p.code;
              const sw =
                p.swatch && p.swatch.length ? p.swatch : [CAT_COLOR[p.category] || '#90A4AE'];
              const band = sw.length > 1 ? `linear-gradient(135deg, ${sw.join(', ')})` : sw[0];
              return (
                <ButtonBase
                  key={p.code}
                  focusRipple
                  onClick={() => applyTemplate(p)}
                  aria-pressed={Boolean(on)}
                  sx={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    borderRadius: 2.5,
                    overflow: 'hidden',
                    border: '1.5px solid',
                    borderColor: on ? 'info.main' : 'grey.300',
                    bgcolor: '#fff',
                    transition: 'all .15s',
                    '&:hover': { borderColor: 'info.main', transform: 'translateY(-2px)' },
                    ...focusSx,
                  }}
                >
                  {p.example ? (
                    <img
                      src={p.example}
                      alt={p.name}
                      style={{ width: '100%', height: 96, objectFit: 'cover', display: 'block' }}
                    />
                  ) : (
                    <MDBox sx={{ height: 96, background: band }} />
                  )}
                  <MDBox p={1.25}>
                    <MDTypography
                      variant='button'
                      fontWeight='bold'
                      display='block'
                      lineHeight={1.3}
                    >
                      {p.name}
                    </MDTypography>
                    <MDTypography variant='caption' color='text' sx={{ opacity: 0.75 }}>
                      {p.description}
                    </MDTypography>
                  </MDBox>
                </ButtonBase>
              );
            })}
          </MDBox>
          {templates.length === 0 && (
            <MDTypography variant='body2' color='text'>
              ยังไม่มีเทมเพลต
            </MDTypography>
          )}
        </DialogContent>
        <DialogActions>
          <TemplateManager
            scope='product'
            app={app}
            defaultPrompt={prompt}
            onChanged={loadMeta}
            onNotify={showNotify}
          />
          <MDButton variant='outlined' color='secondary' onClick={() => setTplOpen(false)}>
            ปิด
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ── dialog เลือกรูปจากแกลเลอรีสินค้า — ตัวเดียวกับหน้าจัดการภาพ (18 ต.ค. 2569) ── */}
      <ImagePickerDialog
        open={galleryOpen}
        multiple
        title='เลือกรูปของสินค้าเป็นรูปอ้างอิง'
        images={images.map((im) => ({ id: im.id, image_url: im.image }))}
        main={{ id: galleryMain ? galleryMain.id : 0, url: galleryMain ? galleryMain.image : '' }}
        disabledIds={refs.filter((r) => r.t === 'g').map((r) => String(r.id))}
        onConfirm={confirmGallery}
        onClose={() => setGalleryOpen(false)}
      />

      <Dialog
        open={Boolean(confirmBox)}
        onClose={() => setConfirmBox(null)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>{confirmBox ? confirmBox.title : ''}</DialogTitle>
        <DialogContent>
          <MDTypography variant='body2'>{confirmBox ? confirmBox.text : ''}</MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton
            autoFocus
            variant='outlined'
            color='secondary'
            onClick={() => setConfirmBox(null)}
          >
            ยกเลิก
          </MDButton>
          <MDButton
            variant='gradient'
            color='dark'
            onClick={() => {
              const box = confirmBox;
              setConfirmBox(null);
              if (box) box.onOk();
            }}
          >
            {confirmBox ? confirmBox.okLabel : ''}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ── dialog แก้ไข — สร้างรอบใหม่จากภาพเดิม ─────────────────────── */}
      <Dialog open={Boolean(editRow)} onClose={() => setEditRow(null)} fullWidth maxWidth='sm'>
        <DialogTitle>แก้ไขภาพ (สร้างรอบใหม่จากภาพนี้)</DialogTitle>
        <DialogContent>
          <MDBox mt={1}>
            <MDInput
              label='อยากแก้อะไรในภาพนี้'
              placeholder='เช่น เปลี่ยนพื้นหลังเป็นสีชมพูอ่อน เพิ่มเงาใต้ขวด'
              fullWidth
              multiline
              rows={3}
              value={editPrompt}
              onChange={(e) => setEditPrompt(e.target.value)}
              error={promptLength(editPrompt) > 2000}
              helperText={
                promptLength(editPrompt) > 1800
                  ? `${promptLength(editPrompt)}/2000 ตัวอักษร${
                      promptLength(editPrompt) > 2000 ? ' — ลดข้อความให้สั้นลง' : ''
                    }`
                  : ''
              }
              InputLabelProps={{ shrink: true }}
            />
            <MDBox mt={2}>
              <MDInput
                label='โมเดล'
                fullWidth
                select
                value={editProvider}
                onChange={(e) => setEditProvider(e.target.value)}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value='auto'>✦ อัตโนมัติ (แนะนำ)</MenuItem>
                {editProviderOptions.map((p) => (
                  <MenuItem key={p.code} value={p.code}>
                    {p.name} · {creditOf(p, editResolution)} เครดิต
                  </MenuItem>
                ))}
              </MDInput>
            </MDBox>
            <MDBox mt={2} display='flex' gap={1.5}>
              <MDInput
                label='สัดส่วน'
                fullWidth
                select
                value={editRatio}
                onChange={(e) => setEditRatio(e.target.value)}
                InputLabelProps={{ shrink: true }}
              >
                {ratioOptions.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                    {RATIO_LABELS[r] ? ` · ${RATIO_LABELS[r]}` : ''}
                  </MenuItem>
                ))}
              </MDInput>
              <MDInput
                label='ความละเอียด'
                fullWidth
                select
                value={editResolution}
                onChange={(e) => setEditResolution(e.target.value)}
                InputLabelProps={{ shrink: true }}
              >
                {resOptions.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </MDInput>
            </MDBox>
            <MDTypography variant='caption' color='text' display='block' mt={1}>
              ได้ภาพใหม่อีก 1 ภาพ ภาพเดิมยังอยู่
            </MDTypography>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton variant='outlined' color='secondary' onClick={() => setEditRow(null)}>
            {t(`element.cancel`)}
          </MDButton>
          <MDButton
            variant='gradient'
            color='dark'
            onClick={saveEdit}
            disabled={
              loading ||
              !editPrompt.trim() ||
              !editPicked ||
              !canGenerate ||
              promptLength(editPrompt) > 2000
            }
          >
            <Icon>auto_awesome</Icon>&nbsp;สร้างภาพใหม่ · ≈ {editCredit ?? '?'} เครดิต
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ── dialog ยืนยันลบ ────────────────────────────────────────────── */}
      <Dialog open={Boolean(delRow)} onClose={() => setDelRow(null)} maxWidth='xs' fullWidth>
        <DialogTitle>ลบภาพนี้ออกจากรายการ?</DialogTitle>
        <DialogContent>
          <MDTypography variant='body2' color='text'>
            ภาพจะหายจากรายการนี้
            {delRow && delRow.approved === 'Y' ? ' (ภาพในแกลเลอรียังอยู่)' : ''}
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton variant='outlined' color='secondary' onClick={() => setDelRow(null)}>
            {t(`element.cancel`)}
          </MDButton>
          <MDButton variant='gradient' color='error' onClick={remove}>
            <Icon>delete</Icon>&nbsp;{t(`element.delete`)}
          </MDButton>
        </DialogActions>
      </Dialog>

      <ImageReview
        items={list
          .filter((row) => row.image_url)
          .map((row) => ({
            id: row.id,
            title: `${productSku || `สินค้า #${id}`} · งาน #${row.id}`,
            src: row.image_url,
            fileName: dlItem(row).name,
            approved: row.approved === 'Y',
            canApprove: row.status === 'SUCCESS' && mayGenerate,
            approveLabel: 'เก็บเข้าแกลเลอรี',
            row,
          }))}
        selectedId={reviewId}
        onChange={setReviewId}
        onClose={() => setReviewId(null)}
        onApprove={approve}
        busy={loading || approving.includes(reviewId)}
      />
      {notify}

      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    </MDBox>
  );
}

ProductAiImage.propTypes = { onOpenGallery: PropTypes.func };
ProductAiImage.defaultProps = { onOpenGallery: null };
export default ProductAiImage;
